/**
 * switch-animations.js — GSAP motion layer (Decisions #29/#30, AST-003)
 *
 * ALL GSAP animations for the Switch theme live in this file, per
 * docs/FRONTEND-STACK.md ("if something moves, GSAP controls it").
 * Each section/feature gets its own init function, registered in
 * INIT_REGISTRY below. Every init must be a no-op when its target
 * elements are absent, so this file stays template-agnostic.
 *
 * Load order (snippets/scripts.liquid, all defer):
 *   gsap.min.js → gsap-scrolltrigger.min.js → this file → alpinejs.min.js
 *
 * Boundary rules (FRONTEND-STACK.md, Switch amendments):
 *   - Never animate elements owned by Horizon web components (cart drawer,
 *     variant picker, dialogs) — Horizon's own motion governs those.
 *   - Components needing motion call the SwitchMotion API exposed here
 *     (window.SwitchMotion) instead of importing gsap themselves — tween
 *     definitions stay in this file.
 *   - Under prefers-reduced-motion every animation either no-ops (the CSS
 *     reduced-motion overrides in switch-custom.css reveal content
 *     statically) or resolves instantly.
 *
 * Migrated from CSS/IO (Decision #30): scroll-reveal palette (was
 * switch-wipe.js + CSS transitions), sl-scroll-carousel auto-marquee (was
 * CSS @keyframes), avisos swap sequence (was transitionend plumbing in
 * switch-avisos.js), hero overlay fade (was switch-image-fade.js + CSS
 * transition).
 */

/* global gsap, ScrollTrigger */

(function () {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* No-GSAP fail-open: reveal everything a tween would have revealed, so
     content is never stranded hidden. Mirrors the old per-script fallbacks. */
  if (typeof gsap === 'undefined') {
    document.addEventListener('DOMContentLoaded', function () {
      document
        .querySelectorAll(
          '.switch-wipe, .switch-fade-up, .switch-blur-in, .switch-scale-soft, .switch-slide-in, .switch-typewriter, .switch-typewriter-loop'
        )
        .forEach(function (el) {
          el.classList.add('is-visible');
        });
      document.querySelectorAll('[data-switch-hero]').forEach(function (el) {
        el.classList.add('is-loaded');
      });
    });
    return;
  }

  if (typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);

    /* The Horizon scroll container moved between versions. Some 4.x builds
       scrolled INSIDE `.page-wrapper` (base.css set html/body overflow:hidden);
       the current build scrolls the WINDOW and leaves `.page-wrapper` at
       overflow:visible. Binding ScrollTrigger to `.page-wrapper` when it is NOT
       the scroll container is actively harmful: its scrollTop is permanently 0,
       so every trigger mis-measures (bogus start/end, all stuck "active") and
       each refresh() forces a full-document reflow — and on iOS Safari the
       resize/refresh churn around a position:fixed drawer can reset scroll
       (jump-to-top) and freeze the main thread. So adopt `.page-wrapper` as the
       scroller ONLY when it is genuinely scrollable; otherwise fall back to
       ScrollTrigger's window default (correct for the current Horizon). */
    var switchScroller = document.querySelector('.page-wrapper');
    if (switchScroller) {
      var overflowY = getComputedStyle(switchScroller).overflowY;
      var scrollerIsScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        switchScroller.scrollHeight > switchScroller.clientHeight;
      if (scrollerIsScrollable) {
        ScrollTrigger.defaults({ scroller: switchScroller });
      }
    }
  }

  function isMobile() {
    return window.innerWidth < 768;
  }

  /* Reveal trigger point. On desktop 'top 85%' fires as the section's top edge
     enters from the bottom — there's plenty of viewport+scroll distance left to
     watch the motion settle. On mobile the viewport is short and scrolling is a
     fast flick, so 'top 85%' fires while the section is still mostly off-screen
     and the ~0.6s reveal finishes before you actually look at it — it reads as
     static. Fire later on mobile (section ~1/3 into view) so the motion plays
     where it can be seen. Mobile fires at 'top 75%' (section ~1/4 into view) — the
     balance between the too-fast desktop default (85%, motion done before seen) and
     65% (felt too late/slow). Tune this one value: higher % = fires sooner/earlier,
     lower % = fires later. (Operator-tuned 2026-06-29.) */
  function revealStart(desktopStart) {
    return isMobile() ? 'top 75%' : desktopStart || 'top 85%';
  }

  /* "Movimento (SL)" per-block tuning (MOD-018, snippets/switch-motion-vars).
     A positive CSS-var value overrides the motion's built-in default; 0/absent
     means "automático" → keep the default. */
  function cssNum(styles, prop) {
    var v = parseFloat(styles.getPropertyValue(prop));
    return v > 0 ? v : 0;
  }

  /* Resolve duration (s) / ease / delay (s) for a reveal element, falling back
     to the per-motion defaults passed in. */
  function revealCfg(el, defDurSec, defEase) {
    var s = getComputedStyle(el);
    var dur = cssNum(s, '--switch-reveal-duration');
    var delay = cssNum(s, '--switch-reveal-delay');
    var ease = s.getPropertyValue('--switch-reveal-ease').trim();
    return {
      duration: dur ? dur / 1000 : defDurSec,
      ease: ease !== '' ? ease : defEase,
      delay: delay ? delay / 1000 : 0,
    };
  }

  /* ----------------------------------------------------------------------
   * Scroll-reveal palette (merchant opt-in via "Custom CSS class" field).
   * Initial hidden states live in switch-custom.css gated under .switch-js;
   * GSAP tweens to the final values (inline style wins over the class).
   * Reduced-motion: the CSS media block reveals statically — skip entirely.
   * Runs on mobile (parity with the previous CSS implementation).
   * -------------------------------------------------------------------- */
  var REVEALS = {
    '.switch-wipe': function (el) {
      var c = revealCfg(el, 0.7, 'power2.inOut');
      return gsap.fromTo(
        el.children,
        { yPercent: 110 },
        { yPercent: 0, duration: c.duration, ease: c.ease, delay: c.delay }
      );
    },
    '.switch-fade-up': function (el) {
      var c = revealCfg(el, 0.6, 'power1.out');
      return gsap.to(el, { opacity: 1, y: 0, duration: c.duration, ease: c.ease, delay: c.delay });
    },
    '.switch-blur-in': function (el) {
      var c = revealCfg(el, 0.7, 'power1.out');
      return gsap.to(el, { opacity: 1, filter: 'blur(0px)', duration: c.duration, ease: c.ease, delay: c.delay });
    },
    '.switch-scale-soft': function (el) {
      var c = revealCfg(el, 0.6, 'power1.out');
      return gsap.to(el, { opacity: 1, scale: 1, duration: c.duration, ease: c.ease, delay: c.delay });
    },
    '.switch-slide-in': function (el) {
      var c = revealCfg(el, 0.6, 'power1.out');
      return gsap.to(el, { opacity: 1, x: 0, duration: c.duration, ease: c.ease, delay: c.delay });
    },
    /* Chunky left-to-right reveal; steps() ease preserves the typewriter feel.
       Tunables (MOD-018) read from CSS vars: --switch-reveal-duration (legacy
       --switch-typewriter-duration still honored), --switch-typewriter-steps,
       --switch-reveal-delay. */
    '.switch-typewriter': function (el) {
      var s = getComputedStyle(el);
      var duration = cssNum(s, '--switch-reveal-duration') || cssNum(s, '--switch-typewriter-duration') || 1200;
      var steps = parseInt(s.getPropertyValue('--switch-typewriter-steps'), 10) || 40;
      var delay = cssNum(s, '--switch-reveal-delay');
      return gsap.to(el, {
        clipPath: 'inset(0% 0% 0% 0%)',
        webkitClipPath: 'inset(0% 0% 0% 0%)',
        duration: duration / 1000,
        ease: 'steps(' + steps + ')',
        delay: delay / 1000,
      });
    },
  };

  function initScrollReveals() {
    if (prefersReducedMotion || typeof ScrollTrigger === 'undefined') return;
    Object.keys(REVEALS).forEach(function (selector) {
      document.querySelectorAll(selector).forEach(function (el) {
        ScrollTrigger.create({
          trigger: el,
          start: revealStart('top 85%'),
          once: true,
          onEnter: function () {
            var tween = REVEALS[selector](el);
            /* End-state lives in the .is-visible CSS class (same as the
               pre-GSAP system), NOT in inline styles: GSAP's residual
               inline transform/opacity would permanently out-rank
               Horizon's :hover transforms (e.g. card-hover-effect-lift /
               -scale in base.css) on any element carrying both. Add the
               class, then clear only the props we tweened. */
            tween.eventCallback('onComplete', function () {
              el.classList.add('is-visible');
              gsap.set(el, { clearProps: 'opacity,transform,filter,clipPath' });
              if (selector === '.switch-wipe') {
                gsap.set(el.children, { clearProps: 'transform' });
              }
            });
          },
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
   * Hero overlay fade (LCP-safe — migrated from switch-image-fade.js).
   * The ::before overlay's opacity is driven by the --switch-hero-overlay
   * custom property on the section; GSAP tweens the property. The overlay
   * stays background-color-only, so it is never an LCP candidate. Reveal
   * fires as soon as ANY <img> in the section finishes loading.
   * -------------------------------------------------------------------- */
  function initHeroFade() {
    document.querySelectorAll('[data-switch-hero]').forEach(function (section) {
      var revealed = false;
      function reveal() {
        if (revealed) return;
        revealed = true;
        if (prefersReducedMotion) {
          section.classList.add('is-loaded');
          return;
        }
        gsap.to(section, {
          '--switch-hero-overlay': 0,
          duration: 0.25,
          ease: 'power1.out',
        });
      }

      var imgs = section.querySelectorAll('img');
      if (!imgs.length) {
        reveal();
        return;
      }
      imgs.forEach(function (img) {
        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
        }
      });
    });
  }

  /* ----------------------------------------------------------------------
   * sl-scroll-carousel auto-marquee (migrated from CSS @keyframes).
   * sl-scroll-carousel.js owns the logic (cloning, IO visibility, hover,
   * editor pauses) and talks to this file via CustomEvents — the stack
   * doc's vanilla-JS ↔ motion bridge:
   *   sl:carousel:ready   {viewport}          → create the loop tween
   *   sl:carousel:pause   {viewport, reason}  → pause (reason-counted)
   *   sl:carousel:resume  {viewport, reason}  → play when no reasons left
   * The track is pre-cloned to 2× content, so xPercent -50 loops seamlessly.
   * Runs on mobile (parity with the CSS marquee); skipped under
   * reduced-motion (parity with the old animation:none media query).
   * -------------------------------------------------------------------- */
  var marquees = new WeakMap();

  function createMarquee(viewport) {
    if (prefersReducedMotion || marquees.has(viewport)) return;
    var track = viewport.querySelector('[data-sl-track]');
    if (!track) return;

    var duration = parseFloat(viewport.dataset.scrollDuration) || 30;
    var reverse = viewport.dataset.direction === 'right';
    var tween = gsap.fromTo(
      track,
      { xPercent: reverse ? -50 : 0 },
      { xPercent: reverse ? 0 : -50, duration: duration, ease: 'none', repeat: -1 }
    );
    marquees.set(viewport, { tween: tween, reasons: new Set() });
  }

  function setMarqueePaused(viewport, reason, paused) {
    var entry = marquees.get(viewport);
    if (!entry) return;
    if (paused) {
      entry.reasons.add(reason);
    } else {
      entry.reasons.delete(reason);
    }
    if (entry.reasons.size > 0) {
      entry.tween.pause();
    } else {
      entry.tween.play();
    }
  }

  function initCarouselMarquees() {
    /* Catch-up: carousels whose ready event fired before this listener
       existed (sl-scroll-carousel.js executes earlier in the defer order). */
    document
      .querySelectorAll('[data-sl-carousel][data-mode="auto"].is-ready')
      .forEach(createMarquee);

    document.addEventListener('sl:carousel:ready', function (e) {
      if (e.detail && e.detail.viewport) createMarquee(e.detail.viewport);
    });
    document.addEventListener('sl:carousel:pause', function (e) {
      if (e.detail && e.detail.viewport) setMarqueePaused(e.detail.viewport, e.detail.reason, true);
    });
    document.addEventListener('sl:carousel:resume', function (e) {
      if (e.detail && e.detail.viewport) setMarqueePaused(e.detail.viewport, e.detail.reason, false);
    });
  }

  /* ----------------------------------------------------------------------
   * SwitchMotion — motion API for Switch components (ES modules execute
   * before this classic-defer script EVALUATES gsap, so components must
   * call these lazily at interaction time and fall back to instant when
   * the API is absent).
   * -------------------------------------------------------------------- */
  /* CSS timing keywords (Theme Editor settings) → GSAP eases. */
  var EASE_MAP = {
    'ease': 'power1.inOut',
    'ease-in': 'power1.in',
    'ease-out': 'power1.out',
    'ease-in-out': 'power1.inOut',
    'linear': 'none',
  };

  window.SwitchMotion = {
    /**
     * Table Images (blocks/switch-table-images.liquid) image swap.
     * Ports the source section's v1.2.0 choreography to GSAP: lock the
     * wrapper height, set both images absolute (is-animating class),
     * animate out+in simultaneously, then release. The caller swaps the
     * .active class after the returned promise resolves.
     * @param {HTMLElement} wrapper — .switch-ti__images
     * @param {HTMLElement} fromImg
     * @param {HTMLElement} toImg
     * @param {{effect: string, duration: number, timing: string, direction: string}} opts
     * @returns {Promise<void>}
     */
    tableImageSwap: function (wrapper, fromImg, toImg, opts) {
      if (prefersReducedMotion) return Promise.resolve();
      var duration = (opts.duration || 300) / 1000;
      var ease = EASE_MAP[opts.timing] || 'power1.inOut';

      return new Promise(function (resolve) {
        gsap.set(wrapper, { height: wrapper.offsetHeight });
        wrapper.classList.add('is-animating');
        fromImg.classList.add('animating-out');
        toImg.classList.add('animating-in');

        var tl = gsap.timeline({
          onComplete: function () {
            wrapper.classList.remove('is-animating');
            fromImg.classList.remove('animating-out');
            toImg.classList.remove('animating-in');
            gsap.set([fromImg, toImg], { clearProps: 'all' });
            gsap.set(wrapper, { clearProps: 'height' });
            resolve(); // caller swaps .active in the same microtask — no flash
          },
        });

        if (opts.effect === 'slide-left' || opts.effect === 'slide-right') {
          var enterFrom = opts.direction === 'next' ? 100 : -100;
          tl.fromTo(toImg, { xPercent: enterFrom }, { xPercent: 0, duration: duration, ease: ease }, 0)
            .to(fromImg, { xPercent: -enterFrom, duration: duration, ease: ease }, 0);
        } else if (opts.effect === 'fade') {
          tl.to(fromImg, { opacity: 0, duration: duration, ease: 'power1.out' }, 0)
            .fromTo(toImg, { opacity: 0 }, { opacity: 1, duration: duration * 0.7, ease: 'power1.in' }, duration * 0.3);
        } else if (opts.effect === 'scale') {
          tl.to(fromImg, { opacity: 0, scale: 0.9, duration: duration, ease: 'power1.out' }, 0)
            .fromTo(
              toImg,
              { opacity: 0, scale: 0.9 },
              { opacity: 1, scale: 1, duration: duration * 0.7, ease: 'power1.in' },
              duration * 0.3
            );
        } else {
          tl.set({}, {}, 0); // unknown effect → resolve via onComplete, instant
        }
      });
    },

    /**
     * Rotating Stack (blocks/switch-rotating-stack.liquid, BLK-007) item swap.
     * The cards overlap inside the JS-locked viewport; this blurs/fades the
     * outgoing card out (with a small vertical drift) while the incoming one
     * blurs/fades in — the "rotate vertically" feel the operator asked for.
     * Anti-hype (§1): short, power2, subtle distance — no bounce. The Alpine
     * component (assets/switch-rotating-stack.js) owns state and swaps the
     * .is-active class AFTER the returned promise resolves (same microtask →
     * no flash, mirroring tableImageSwap).
     * @param {HTMLElement} viewport — .switch-rs__viewport
     * @param {HTMLElement} fromEl
     * @param {HTMLElement} toEl
     * @param {{duration: number, direction: string}} opts
     * @returns {Promise<void>}
     */
    rotatingStackSwap: function (viewport, fromEl, toEl, opts) {
      if (prefersReducedMotion) return Promise.resolve();
      var dur = ((opts && opts.duration) || 600) / 1000;
      var dir = opts && opts.direction === 'prev' ? -1 : 1;

      return new Promise(function (resolve) {
        viewport.classList.add('is-animating');
        var tl = gsap.timeline({
          onComplete: function () {
            viewport.classList.remove('is-animating');
            gsap.set([fromEl, toEl], { clearProps: 'opacity,filter,transform' });
            resolve();
          },
        });
        tl.to(
          fromEl,
          { opacity: 0, filter: 'blur(10px)', yPercent: -6 * dir, duration: dur * 0.55, ease: 'power2.in' },
          0
        ).fromTo(
          toEl,
          { opacity: 0, filter: 'blur(10px)', yPercent: 8 * dir },
          { opacity: 1, filter: 'blur(0px)', yPercent: 0, duration: dur * 0.6, ease: 'power2.out' },
          dur * 0.4
        );
      });
    },

    /**
     * Collapse-fade a row out, swap its content, expand-fade back in.
     * Used by switch-avisos.js on variant changes (Decision #30 — replaces
     * the transitionend/double-rAF/safety-timer plumbing).
     * @param {HTMLElement} row
     * @param {Function} swapContent — called between out and in
     * @returns {Promise<void>}
     */
    collapseSwap: function (row, swapContent) {
      if (prefersReducedMotion) {
        swapContent();
        return Promise.resolve();
      }
      return new Promise(function (resolve) {
        var tl = gsap.timeline({
          onComplete: function () {
            gsap.set(row, { clearProps: 'height,opacity,overflow' });
            resolve();
          },
        });
        tl.set(row, { overflow: 'hidden' })
          .to(row, { height: 0, opacity: 0, duration: 0.2, ease: 'power1.in' })
          .add(function () {
            swapContent();
            /* measure the rebuilt content, then animate to it */
            gsap.set(row, { height: 'auto' });
            var target = row.offsetHeight;
            gsap.set(row, { height: 0 });
            tl.to(row, { height: target, opacity: 1, duration: 0.2, ease: 'power1.out' });
          });
      });
    },
  };

  /* ----------------------------------------------------------------------
   * "A Solução" section — staggered reveal of the frosted-glass benefit
   * cards as the row scrolls into view. Subtle/anti-hype (§1): short rise +
   * micro-scale, power3 ease, 0.12s stagger. Reduced-motion → no-op (cards
   * are never hidden). End-state is cleared so residual inline transforms
   * don't out-rank any future hover styles. Scoped to the "Baixo" row by its
   * block key group_bBftCF via [class*=] — Horizon prefixes nested block ids
   * with a random hash in the rendered class (see switch-custom.css header),
   * so a plain `.group-block--group_bBftCF` selector would match nothing.
   * -------------------------------------------------------------------- */
  function initSolucaoCards() {
    if (prefersReducedMotion || typeof ScrollTrigger === 'undefined') return;
    var cards = document.querySelectorAll(
      '[class*="group_bBftCF"] > .group-block-content > .group-block'
    );
    if (!cards.length) return;
    gsap.set(cards, { opacity: 0, y: 28, scale: 0.97 });
    ScrollTrigger.create({
      trigger: cards[0].parentNode,
      start: revealStart('top 80%'),
      once: true,
      onEnter: function () {
        gsap.to(cards, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.7,
          ease: 'power3.out',
          stagger: 0.12,
          onComplete: function () {
            gsap.set(cards, { clearProps: 'opacity,transform' });
          },
        });
      },
    });
  }

  /* ----------------------------------------------------------------------
   * Section entrance reveals — a subtle fade-up as each main-content section
   * scrolls into view (operator request 2026-06-22; the "A Solução" card
   * stagger is the north — same opacity + short rise + power3.out feel, §1
   * anti-hype). PERFORMANCE-FIRST, by design:
   *   - Only sections that start FULLY BELOW the fold are hidden+revealed, so
   *     above-the-fold / LCP content always paints immediately and is never
   *     stranded at opacity 0.
   *   - Skipped under prefers-reduced-motion and inside the Theme Editor
   *     (Shopify.designMode) so editing always shows everything.
   *   - GPU-only props (opacity + transform), once:true (no ongoing work),
   *     and props are cleared on finish so a residual inline transform never
   *     out-ranks Horizon hovers or breaks a sticky descendant.
   *   - No-GSAP path never hides anything (the whole file early-returns), so
   *     content can't be stranded if the library fails to load.
   * Runs on mobile (lightweight — parity with the other reveals). The hero
   * (owns initHeroFade) and "A Solução" (owns initSolucaoCards) are skipped.
   * -------------------------------------------------------------------- */
  function initSectionReveals() {
    if (prefersReducedMotion || typeof ScrollTrigger === 'undefined') return;
    if (window.Shopify && window.Shopify.designMode) return;
    var container = document.querySelector('#MainContent') || document.querySelector('.content-for-layout');
    if (!container) return;
    var vh = window.innerHeight;
    [].slice.call(container.children).forEach(function (section) {
      if (!section.classList || !section.classList.contains('shopify-section')) return;
      if (section.hasAttribute('data-switch-hero')) return; // hero owns initHeroFade
      if (section.querySelector('[class*="group_bBftCF"]')) return; // A Solução owns its card stagger
      if (section.getBoundingClientRect().top < vh) return; // above/at the fold → never hide (LCP-safe)
      gsap.set(section, { opacity: 0, y: 24 });
      ScrollTrigger.create({
        trigger: section,
        start: revealStart('top 85%'),
        once: true,
        onEnter: function () {
          gsap.to(section, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power3.out',
            onComplete: function () {
              gsap.set(section, { clearProps: 'opacity,transform' });
            },
          });
        },
      });
    });
  }

  /* ----------------------------------------------------------------------
   * Looping typewriter ('.switch-typewriter-loop', text/heading blocks via
   * the "Movimento (SL)" dropdown — MOD-016/MOD-017). Continuously type →
   * hold → erase → repeat, via a clip-path inset tween with a steps() ease.
   * Continuous, so it PAUSES offscreen (IntersectionObserver) for perf; the
   * element starts clipped via CSS, so it stays hidden until it first plays.
   * Reduced-motion: skip entirely (the CSS media block un-clips → static
   * text). No-GSAP: the fail-open above adds .is-visible → CSS un-clips.
   * Tunables via CSS custom props (defaults below) — same family as the
   * one-shot typewriter, plus hold/erase.
   * -------------------------------------------------------------------- */
  function initTypewriterLoop() {
    if (prefersReducedMotion) return;
    document.querySelectorAll('.switch-typewriter-loop').forEach(function (el) {
      var styles = getComputedStyle(el);
      var typeDur = (cssNum(styles, '--switch-reveal-duration') || cssNum(styles, '--switch-typewriter-duration') || 1600) / 1000;
      var steps = parseInt(styles.getPropertyValue('--switch-typewriter-steps'), 10) || 24;
      var hold = (cssNum(styles, '--switch-typewriter-hold') || 1400) / 1000;
      var eraseDur = (cssNum(styles, '--switch-typewriter-erase') || 800) / 1000;
      var delay = cssNum(styles, '--switch-reveal-delay') / 1000;
      var ease = 'steps(' + steps + ')';

      var tl = gsap.timeline({ repeat: -1, paused: true, delay: delay });
      tl.set(el, { clipPath: 'inset(0 100% 0 0)' })
        .to(el, { clipPath: 'inset(0 0% 0 0)', duration: typeDur, ease: ease }) // type in (L→R)
        .to(el, {}, '+=' + hold) // hold full
        .to(el, { clipPath: 'inset(0 100% 0 0)', duration: eraseDur, ease: ease }) // erase (R→L)
        .to(el, {}, '+=0.3'); // brief pause before retyping

      /* Play only while on screen — continuous loops are wasteful offscreen.
         Uses ScrollTrigger (layout geometry), NOT IntersectionObserver: IO
         mis-reports some in-view elements as not-intersecting when an ancestor
         container/overflow is involved, which left the loop stuck paused.
         onToggle handles enter/leave; the isActive check covers an element
         already on screen at load (onToggle wouldn't fire for it). */
      if (typeof ScrollTrigger !== 'undefined') {
        var st = ScrollTrigger.create({
          trigger: el,
          start: 'top bottom',
          end: 'bottom top',
          onToggle: function (self) {
            if (self.isActive) tl.play();
            else tl.pause();
          },
        });
        if (st.isActive) tl.play();
      } else {
        tl.play();
      }
    });
  }

  /* ----------------------------------------------------------------------
   * Registry. { heavy: true } inits are skipped under reduced-motion and
   * on mobile. Reveals/marquee/hero handle reduced-motion internally and
   * MUST run on mobile (parity with the CSS behaviors they replaced).
   * -------------------------------------------------------------------- */
  var INIT_REGISTRY = [
    { init: initScrollReveals },
    { init: initHeroFade },
    { init: initCarouselMarquees },
    { init: initSolucaoCards },
    { init: initSectionReveals },
    { init: initTypewriterLoop },
  ];

  function runInits() {
    INIT_REGISTRY.forEach(function (entry) {
      if (entry.heavy && (prefersReducedMotion || isMobile())) return;
      try {
        entry.init();
      } catch (error) {
        console.error('[switch-animations]', error);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', runInits);

  /* Horizon re-renders sections dynamically (morph, Theme Editor) — scroll
     positions shift, so ScrollTrigger must re-measure. */
  if (typeof ScrollTrigger !== 'undefined') {
    document.addEventListener('shopify:section:load', function () {
      ScrollTrigger.refresh();
    });
    document.addEventListener('cart:update', function () {
      ScrollTrigger.refresh();
    });
  }
})();
